import { useMemo } from 'react';
import styled, { type CSSProperties } from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { Typography } from '@elfsight/widget-ui';
import { useResizeObserver } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { fieldsStyles } from '../../form/fields-styles';
import { AnimatedNumber } from './animated-number';
import type { ResultProps } from './result';
import { useAdaptiveFontSize } from './use-adaptive-font-size';

export function ResultSecondary({
  calculation,
  formatter,
  value
}: ResultProps) {
  const { fieldStyle } = useSettings();
  const textStyle = fieldsStyles[fieldStyle].fieldValue;
  const { containerRef, textRef, adaptiveFontSize } = useAdaptiveFontSize(
    textStyle,
    value
  );

  const [textRect] = useResizeObserver(textRef.current);
  const resultHeight = useMemo(() => textRect.height, [textRect.height]);
  const isString = typeof value === 'string';
  const isMultilineResult = isString && resultHeight / 20 > 1;

  const formattedValue = formatter ? formatter(value) : value;
  const resultStyle: CSSProperties = {
    fontSize: isString ? 15 : adaptiveFontSize,
    position: 'absolute',
    inset: 0,
    left: -8
  };

  return (
    <SecondaryContainer>
      <SecondaryLabel>
        <Show when={!!calculation.name}>
          <Typography
            textStyle={{ lineHeight: '20px', fontSize: 15, fontWeight: 'bold' }}
          >
            {calculation.name}
          </Typography>
        </Show>

        <Show when={!!calculation.caption}>
          <Typography
            textStyle={{ lineHeight: '16px', fontSize: 13 }}
            style={{ opacity: 0.7 }}
            dangerouslySetInnerHTML={{ __html: calculation.caption }}
          />
        </Show>
      </SecondaryLabel>
      <Value
        ref={containerRef}
        textStyle={{
          ...textStyle,
          lineHeight: '20px',
          fontSize: isString ? 15 : adaptiveFontSize
        }}
        _withWrap={isString}
        _isMultiline={isMultilineResult}
      >
        <HiddenValue ref={textRef} style={{ fontSize: textStyle.fontSize }}>
          {formattedValue}
        </HiddenValue>

        <Show when={isString}>
          <Typography
            textStyle={{ lineHeight: 'inherit', fontWeight: 'bold' }}
            style={resultStyle}
          >
            {formattedValue}
          </Typography>
        </Show>

        <Show when={!isString}>
          <AnimatedNumber
            formatter={formatter}
            value={Number(value)}
            style={{
              ...resultStyle,
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          />
        </Show>
      </Value>
    </SecondaryContainer>
  );
}

const HiddenValue = styled.span`
  display: inline-block;
  opacity: 0;
  pointer-events: none;
`;

const Value = styled(Typography)<{ _withWrap: boolean; _isMultiline: boolean }>`
  flex-grow: 2;
  flex-shrink: 1;
  margin-left: auto;
  min-width: 80px;
  max-width: 100%;
  position: relative;

  white-space: ${({ _withWrap }) => (_withWrap ? 'normal' : 'nowrap')};
  text-align: ${({ _isMultiline }) => (_isMultiline ? 'left' : 'right')};
`;

const SecondaryLabel = styled.div`
  flex-grow: 1;
  flex-shrink: 2;
  min-width: 120px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SecondaryContainer = styled.div`
  justify-content: space-between;
  gap: 16px;
  overflow: hidden;
  display: flex;
`;
